"use client";

import type { ModuleProductIntro } from "@lib/types";
import { useState } from "react";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Container } from "@/components/ui/container";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/useMobile";

type IntroProps = {
  module: ModuleProductIntro;
};

export function Intro({ module }: IntroProps) {
  const [activeItem, setActiveItem] = useState<string | null>(() => {
    return module.highlights?.[0]?._key || null;
  });

  const isMobile = useIsMobile();

  if (!module.highlights || module.highlights.length === 0) return null;

  const handleItemInteraction = (key: string) => {
    if (isMobile) {
      setActiveItem(activeItem === key ? null : key);
    } else {
      setActiveItem(key);
    }
  };

  return (
    <section className="bg-neutral-lightest py-padding-section-lg">
      <Container>
        <div className="text-text-dark mb-padding-section-sm max-w-max-width-lg">
          <h2 className="typography-heading-3">{module.title}</h2>
          {module.body && (
            <p className="typography-text-medium-normal mt-gap-element-md">{module.body}</p>
          )}
        </div>

        <div className="xl:pt-padding-element-xl">
          <div className="gap-gap-element-xl grid items-start xl:grid-cols-4">
            <div className="col-span-3 flex justify-start md:col-span-1">
              <div className="bg-emerald-darkest px-padding-element-sm py-padding-element-2xs inline-flex items-center rounded-full">
                <span className="text-medium-medium text-emerald font-medium">
                  {module.eyebrow}
                </span>
              </div>
            </div>

            <div className="col-span-3">
              {module.highlights.map((feature, index: number) => (
                <div key={feature._key}>
                  <div
                    className={`transition-all duration-1000 ease-out ${
                      activeItem === feature._key ? "rounded-lg bg-white" : "bg-transparent"
                    }`}
                  >
                    <button
                      className="w-full text-left"
                      onClick={() => handleItemInteraction(feature._key)}
                      onMouseEnter={() => !isMobile && handleItemInteraction(feature._key)}
                    >
                      <div className="overflow-hidden">
                        {activeItem === feature._key ? (
                          <div className="md:gap-gap-element-lg animate-in fade-in-0 slide-in-from-top-1 flex flex-col-reverse duration-1000 md:flex-row md:items-center">
                            <div className="gap-gap-element-sm px-padding-element-sm pt-padding-section-sm pb-padding-element-sm md:gap-gap-element-xl md:pl-padding-section-sm flex flex-col md:w-2/3 md:pt-0">
                              <h4 className="typogaphy-heading-4 text-black">{feature.title}</h4>
                              <div>
                                <p className="text-text-subtle">{feature.description}</p>
                              </div>
                            </div>
                            {feature.media ? (
                              <div className="max-h-[322px] w-full overflow-hidden rounded-t-lg bg-neutral-900 md:w-1/3 md:rounded-t-none md:rounded-r-lg">
                                <ResponsiveImage
                                  src={feature.media}
                                  width={320}
                                  height={320}
                                  className="h-full w-full object-cover object-center"
                                  imageOptions={{ desktopWidth: 320 }}
                                  useNextImage
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <h4 className="typogaphy-heading-4 ml-padding-element-sm md:ml-padding-section-sm text-black">
                            {feature.title}
                          </h4>
                        )}
                      </div>
                    </button>
                  </div>

                  {index < module.highlights.length - 1 && (
                    <Separator className="my-gap-element-lg md:ml-gap-element-xl md:max-w-[95%]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
